import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPaperPlane, FaPlus } from 'react-icons/fa';
import { FaUserCircle } from 'react-icons/fa';
import useSocket from '../../../Hooks/useSocket';
import useUserStore from '../../../Stores/UserStore';
import { showToast } from '../../../Components/Toast/toast';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import CustomModal from '../../../Components/CustomModal';
import './WatchTogether.css';
import { FaArrowLeftLong } from 'react-icons/fa6';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';
import watchPlaceholder from '../../../assets/images/watch-together-figma-placeholder.png';

const WatchTogether = () => {
  usePageTitle('Watch Together');
  const navigate = useNavigate();
  const { roomId: chatId } = useParams(); // roomId is actually chat_id
  const { user } = useUserStore();
  const socket = useSocket();
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL || '';

  const [videoId, setVideoId] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [player, setPlayer] = useState(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [showVideoControls, setShowVideoControls] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const playerRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const videoIdRef = useRef('');
  const playerInstanceRef = useRef(null);
  const isPlayerReadyRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    videoIdRef.current = videoId;
  }, [videoId]);

  useEffect(() => {
    playerInstanceRef.current = player;
    isPlayerReadyRef.current = isPlayerReady;
  }, [player, isPlayerReady]);

  // Extract YouTube video ID from URL
  const extractVideoId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const getRenderableMessageText = (msg) => {
    if (msg == null) return '';
    if (typeof msg === 'string' || typeof msg === 'number') return String(msg);
    if (typeof msg === 'object') {
      if (typeof msg.message === 'string' || typeof msg.message === 'number') return String(msg.message);
      if (typeof msg.text === 'string' || typeof msg.text === 'number') return String(msg.text);
      return '';
    }
    return '';
  };

  const resolveImageSrc = (pathOrUrl) => {
    if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    if (!imageBaseUrl) return pathOrUrl;

    const base = imageBaseUrl.endsWith('/') ? imageBaseUrl.slice(0, -1) : imageBaseUrl;
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${base}${path}`;
  };

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      if (playerRef.current && !player) {
        initializePlayer();
      }
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      // Wait for API to be ready
      const checkAPI = setInterval(() => {
        if (window.YT && window.YT.Player) {
          clearInterval(checkAPI);
          if (playerRef.current && !player) {
            initializePlayer();
          }
        }
      }, 100);
      return () => clearInterval(checkAPI);
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      if (playerRef.current && !player) {
        initializePlayer();
      }
    };

    return () => {
      if (window.onYouTubeIframeAPIReady) {
        delete window.onYouTubeIframeAPIReady;
      }
    };
  }, [player]);

  // Initialize YouTube Player
  const initializePlayer = () => {
    if (playerRef.current && window.YT && window.YT.Player && !player) {
      try {
        const playerConfig = {
          height: '100%',
          width: '100%',
          playerVars: {
            autoplay: 0,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            enablejsapi: 1,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              setPlayer(event.target);
              setIsPlayerReady(true);
              // Only load video if we have a valid videoId
              if (videoId && videoId.trim() && videoId.length === 11) {
                try {
                  event.target.loadVideoById(videoId);
                } catch (error) {
                  console.error('Error loading video on ready:', error);
                }
              }
            },
            onStateChange: (event) => {
              handlePlayerStateChange(event);
            },
            onError: (event) => {
              console.error('YouTube Player Error:', event.data);
              let errorMessage = 'Error loading video. ';
              switch (event.data) {
                case 2:
                  errorMessage += 'Invalid video ID.';
                  break;
                case 5:
                  errorMessage += 'HTML5 player error.';
                  break;
                case 100:
                  errorMessage += 'Video not found.';
                  break;
                case 101:
                case 150:
                  errorMessage += 'Video not allowed to be played in embedded players.';
                  break;
                default:
                  errorMessage += 'Please check the URL.';
              }
              showToast(errorMessage, 'error');
            },
          },
        };

        // Use a placeholder video if no videoId is provided
        if (videoId && videoId.trim() && videoId.length === 11) {
          playerConfig.videoId = videoId;
        } else {
          // No default video - start with empty player
          playerConfig.videoId = '';
        }

        const ytPlayer = new window.YT.Player(playerRef.current, playerConfig);
      } catch (error) {
        console.error('Error initializing YouTube player:', error);
        showToast('Failed to initialize video player. Please refresh the page.', 'error');
      }
    }
  };

  // Handle player state changes
  const handlePlayerStateChange = (event) => {
    if (isSyncing) return;

    const state = event.data;
    const newIsPlaying = state === window.YT.PlayerState.PLAYING;
    const newCurrentTime = player?.getCurrentTime() || 0;
    setIsPlaying(newIsPlaying);

    // Debounce sync events
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      if (chatId) {
        socket.syncVideoState(chatId, {
          is_playing: newIsPlaying,
          current_time: newCurrentTime,
          video_id: videoId,
          user_id: user?.id,
        });
      }
    }, 500);
  };

  const handlePickVideo = () => {
    setShowVideoControls((prev) => !prev);
  };

  const handleVideoUrlChange = (e) => {
    setVideoUrl(e.target.value);
  };

  const handleLoadVideo = () => {
    const extractedId = extractVideoId(videoUrl);
    if (!extractedId || extractedId.length !== 11) {
      showToast('Invalid YouTube URL. Please enter a valid YouTube link or video ID.', 'error');
      return;
    }

    setVideoId(extractedId);
    videoIdRef.current = extractedId;

    if (player && isPlayerReady) {
      try {
        setIsSyncing(true);
        player.loadVideoById(extractedId);
        if (chatId) {
          socket.changeVideo(chatId, extractedId, user?.id);
        }
        setTimeout(() => setIsSyncing(false), 1000);
      } catch (error) {
        showToast('Failed to load video. Please try again.', 'error');
        setIsSyncing(false);
      }
    } else if (!isPlayerReady) {
      showToast('Player is loading. Video will load shortly.', 'info');
      if (chatId) {
        socket.changeVideo(chatId, extractedId, user?.id);
      }
    }
  };

  const handlePlayPause = () => {
    if (!player || !isPlayerReady) return;

    setIsSyncing(true);
    if (isPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
    setTimeout(() => setIsSyncing(false), 500);
  };

  // Send message
  const handleSendMessage = () => {
    if (!messageText.trim() || !chatId) return;

    socket.sendWatchMessage(
      chatId,
      messageText,
      user?.id,
      user?.full_name || user?.username,
      user?.image
    );

    // Add message to local state immediately
    setMessages((prev) => [
      ...prev,
      {
        user_id: user?.id,
        user_name: user?.full_name || user?.username,
        user_image: user?.image,
        message: messageText,
        timestamp: new Date().toISOString(),
      },
    ]);
    setMessageText('');
  };

  // Handle leave room
  const handleLeave = () => {
    if (chatId) {
      socket.leaveWatchRoom(chatId);
    }
    navigate('/chat');
  };

  const handleLeaveConfirm = () => {
    setShowLeaveConfirm(false);
    handleLeave();
  };

  // Socket event handlers
  useEffect(() => {
    if (!chatId) {
      showToast('No chat ID provided', 'error');
      navigate('/chat');
      return;
    }

    console.log('=== WATCH TOGETHER INIT ===');
    console.log('Chat ID:', chatId);
    console.log('User:', user?.id, user?.full_name);

    // Join room: wt-{chatId}
    socket.joinWatchRoom(chatId);

    // Handle watchTogetherEvent
    const handleWatchTogetherEvent = (data) => {
      console.log('📩 Received watchTogetherEvent:', data);
      console.log('My user ID:', user?.id);
      console.log('Event user ID:', data.user_id);

      // For messages, ignore own events (we add them locally)
      // For video events, we might receive our own echoed back - still process if video_id is different
      const isOwnEvent = data.user_id && data.user_id === user?.id;
      
      if (isOwnEvent && data.action === 'message') {
        console.log('Ignoring own message event');
        return;
      }

      // For video events, only ignore if it's our own AND we already have that video
      if (isOwnEvent && (data.action === 'sync' || data.action === 'change_video')) {
        if (data.video_id === videoIdRef.current) {
          console.log('Ignoring own video event - already have this video');
          return;
        }
      }

      switch (data.action) {
        case 'sync':
          handleVideoSync(data);
          break;
        case 'change_video':
          handleVideoChange(data);
          break;
        case 'message':
          handleWatchMessage(data);
          break;
        case 'user_joined':
          handleUserJoined(data);
          break;
        case 'user_left':
          handleUserLeft(data);
          break;
        default:
          console.log('Unknown action, trying to handle as video data:', data);
          // Try to handle as video data if it has video_id
          if (data.video_id) {
            handleVideoChange(data);
          }
      }
    };

    // Handle room-specific events (wt-{chatId})
    const handleRoomEvent = (data) => {
      console.log(`📩 Received event on wt-${chatId}:`, data);
      
      // For room events, always try to process video data
      const isOwnEvent = data.user_id && data.user_id === user?.id;
      
      if (isOwnEvent && data.action === 'message') {
        console.log('Ignoring own message event from room');
        return;
      }

      switch (data.action) {
        case 'sync':
          handleVideoSync(data);
          break;
        case 'change_video':
          handleVideoChange(data);
          break;
        case 'message':
          handleWatchMessage(data);
          break;
        case 'user_joined':
          handleUserJoined(data);
          break;
        case 'user_left':
          handleUserLeft(data);
          break;
        default:
          // Handle if data is sent directly without action field
          console.log('No action field, checking for video/message data');
          if (data.video_id) {
            console.log('Found video_id, treating as video change');
            handleVideoChange(data);
          } else if (data.is_playing !== undefined) {
            console.log('Found is_playing, treating as video sync');
            handleVideoSync(data);
          } else if (data.message) {
            console.log('Found message, treating as chat message');
            handleWatchMessage(data);
          }
      }
    };

    const handleVideoSync = (data) => {
      console.log('=== VIDEO SYNC ===');
      console.log('Received video_id:', data.video_id);
      console.log('Current videoId:', videoIdRef.current);
      console.log('Player ready:', isPlayerReadyRef.current);
      
      setIsSyncing(true);
      
      // Always update videoId if different
      if (data.video_id && data.video_id !== videoIdRef.current) {
        console.log('Updating videoId to:', data.video_id);
        setVideoId(data.video_id);
        videoIdRef.current = data.video_id;
        
        if (playerInstanceRef.current && isPlayerReadyRef.current) {
          console.log('Loading video via sync:', data.video_id);
          playerInstanceRef.current.loadVideoById(data.video_id);
        }
      }

      if (playerInstanceRef.current && isPlayerReadyRef.current) {
        const currentTime = playerInstanceRef.current.getCurrentTime() || 0;
        const timeDiff = Math.abs(currentTime - (data.current_time || 0));
        
        if (timeDiff > 2 && data.current_time) {
          console.log('Seeking to:', data.current_time);
          playerInstanceRef.current.seekTo(data.current_time, true);
        }

        if (data.is_playing === true) {
          console.log('Playing video');
          playerInstanceRef.current.playVideo();
        } else if (data.is_playing === false) {
          console.log('Pausing video');
          playerInstanceRef.current.pauseVideo();
        }
      }

      setTimeout(() => setIsSyncing(false), 1000);
    };

    const handleVideoChange = (data) => {
      console.log('=== VIDEO CHANGE ===');
      console.log('Received video_id:', data.video_id);
      console.log('Current videoId:', videoIdRef.current);
      console.log('Player ready:', isPlayerReadyRef.current);
      
      if (data.video_id) {
        // Always update the videoId when receiving change event
        console.log('Setting videoId to:', data.video_id);
        setVideoId(data.video_id);
        videoIdRef.current = data.video_id;
        setVideoUrl(`https://www.youtube.com/watch?v=${data.video_id}`);
        
        if (playerInstanceRef.current && isPlayerReadyRef.current) {
          console.log('Loading video via change:', data.video_id);
          setIsSyncing(true);
          playerInstanceRef.current.loadVideoById(data.video_id);
          setTimeout(() => setIsSyncing(false), 1000);
        } else {
          console.log('Player not ready, video will load when ready');
        }
      }
    };

    const handleWatchMessage = (data) => {
      setMessages((prev) => [...prev, data]);
    };

    const handleUserJoined = (data) => {
      showToast(`${data.user_name || 'A user'} joined the room`, 'info');
    };

    const handleUserLeft = (data) => {
      showToast(`${data.user_name || 'A user'} left the room`, 'info');
    };

    // Listen to watchTogetherEvent
    socket.onWatchTogetherEvent(handleWatchTogetherEvent);

    // Also listen to room-specific channel (wt-{chatId})
    socket.onWatchRoom(chatId, handleRoomEvent);

    return () => {
      socket.offWatchTogetherEvent(handleWatchTogetherEvent);
      socket.offWatchRoom(chatId, handleRoomEvent);
      if (chatId) {
        socket.leaveWatchRoom(chatId);
      }
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [chatId, user?.id, socket]); // Reduced dependencies - handlers use refs now

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cleanup player on unmount
  useEffect(() => {
    return () => {
      if (player && typeof player.destroy === 'function') {
        try {
          player.destroy();
        } catch (error) {
          console.error('Error destroying player:', error);
        }
      }
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [player]);

  // Load video when videoId changes and player is ready
  useEffect(() => {
    console.log('=== VIDEO LOAD EFFECT ===');
    console.log('videoId:', videoId);
    console.log('player:', !!player);
    console.log('isPlayerReady:', isPlayerReady);
    
    // Skip if no valid videoId or player not ready
    if (!videoId || !videoId.trim() || videoId.length !== 11 || !player || !isPlayerReady) {
      console.log('Skipping load - conditions not met');
      return;
    }
    
    // Skip placeholder video
    if (!videoId || videoId.trim() === '') {
      console.log('Skipping placeholder video');
      return;
    }
    
    try {
      const currentVideoData = player.getVideoData();
      const currentVideoId = currentVideoData?.video_id;
      console.log('Current player videoId:', currentVideoId);
      
      // Load if different video
      if (currentVideoId !== videoId) {
        console.log('🎬 Loading new video:', videoId);
        player.loadVideoById(videoId);
      } else {
        console.log('Video already loaded');
      }
    } catch (error) {
      console.error('Error loading video in useEffect:', error);
      // Try to load anyway
      try {
        player.loadVideoById(videoId);
      } catch (e) {
        console.error('Retry also failed:', e);
      }
    }
  }, [videoId, player, isPlayerReady]);

  return (
    <>
      <section className="watch-together-figma-page">
        <img src={heartDecor} alt="" className="watch-together-heart" aria-hidden="true" />
        <div className="watch-together-hero">
          <Container className="watch-together-hero-inner">
            <button
              className="watch-together-back-btn"
              onClick={() => navigate('/chat')}
              aria-label="Go back to chat"
            >
              <FaArrowLeftLong />
            </button>
            <h1 className="watch-together-title">Watch Together</h1>
          </Container>
        </div>

        <div className="watch-together-main">
          <Container className="watch-together-figma-container">
            <div className="watch-together-figma-card">
              <div className="watch-content-layout">
                <div className="watch-video-panel">
                  {showVideoControls && (
                    <div className="watch-video-url-row">
                      <input
                        type="text"
                        placeholder="Enter YouTube URL or Video ID"
                        value={videoUrl}
                        onChange={handleVideoUrlChange}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleLoadVideo();
                          }
                        }}
                        className="watch-video-url-input"
                      />
                      <button
                        className="watch-load-video-btn"
                        onClick={handleLoadVideo}
                        disabled={!videoUrl.trim()}
                      >
                        Load Video
                      </button>
                    </div>
                  )}

                  <div className="watch-video-shell">
                    <div className="youtube-player-container">
                      <div ref={playerRef} id="youtube-player"></div>
                      {!videoId && (
                        <div className="watch-placeholder-overlay">
                          <img src={watchPlaceholder} alt="" className="watch-placeholder-image" />
                        </div>
                      )}
                      {!isPlayerReady && (
                        <div className="player-loading">
                          <p>Loading player...</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="watch-video-actions">
                    <button
                      className="watch-action-btn watch-add-btn"
                      onClick={handlePickVideo}
                      aria-label="Add YouTube video"
                    >
                      <FaPlus />
                    </button>
                    <button
                      className="watch-play-btn"
                      onClick={handlePlayPause}
                      disabled={!isPlayerReady || !videoId}
                    >
                      {isPlaying ? 'Pause' : 'Play'}
                    </button>
                    <button
                      className="watch-leave-btn"
                      onClick={() => setShowLeaveConfirm(true)}
                    >
                      Leave Room
                    </button>
                  </div>
                </div>

                <div className="watch-chat-panel">
                  <h5 className="watch-chat-title">Chat</h5>
                  <div className="watch-chat-messages">
                    {messages.length === 0 ? (
                      <p className="watch-chat-empty">No messages yet. Start the conversation!</p>
                    ) : (
                      messages.map((msg, idx) => (
                        <div key={`${msg.timestamp || ''}-${idx}`} className={`watch-chat-message ${msg.user_id === user?.id ? 'own' : ''}`}>
                          <div className="watch-chat-avatar">
                            {msg.user_image ? (
                              <img
                                src={resolveImageSrc(msg.user_image)}
                                alt={msg.user_name || 'User'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            <FaUserCircle style={{ display: msg.user_image ? 'none' : 'block' }} />
                          </div>
                          <div className="watch-chat-message-content">
                            <div className="watch-chat-message-meta">
                              <span>{msg.user_id === user?.id ? 'You' : msg.user_name || 'User'}</span>
                              <span>
                                {msg.timestamp
                                  ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                  : ''}
                              </span>
                            </div>
                            <p>{getRenderableMessageText(msg.message)}</p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="watch-chat-input-row">
                    <input
                      type="text"
                      placeholder="Type Something..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSendMessage();
                        }
                      }}
                      className="watch-message-input"
                    />

                    <button
                      className="watch-action-btn watch-send-btn"
                      onClick={handleSendMessage}
                      disabled={!messageText.trim()}
                      aria-label="Send message"
                    >
                      <FaPaperPlane />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </section>

      <CustomModal
        show={showLeaveConfirm}
        close={() => setShowLeaveConfirm(false)}
        showImage
        variant="info"
        showTitle
        title="Are you sure?"
        description="Do you want to leave this room?"
        btn1Text="Yes"
        btn2Text="No"
        action={handleLeaveConfirm}
      />
    </>
  );
};

export default WatchTogether;
